import {Injectable} from '@angular/core';
import {EventService} from './event.service';
import {Observable, switchMap, of, Subscription, combineLatest} from 'rxjs';
import {flatMap, UIArtefakt, UiBaseControl} from '../models/uiArtefakt';
import {normalizeVermittlernummer, Vermittlerdaten} from '../models/vermittlerdaten';
import {catchError, filter, map, take} from 'rxjs/operators';
import {UiControl} from '../models/uiControl';
import {KeycloakLv1871Service, StringUtils, UiConfigService} from '@lv1871/core';
import {HttpClient} from '@angular/common/http';

export interface VermittlerAvvData {
  vermittlernummer: string;
}

@Injectable()
export class VermittlerdatenService {
  private _subscription = Subscription.EMPTY;

  constructor(private _http: HttpClient, private _authService: KeycloakLv1871Service, private _eventService: EventService,
              private _uiConfigService: UiConfigService, private _keycloak: KeycloakLv1871Service) {
    this._subscription.unsubscribe();
    this._subscription = combineLatest(
        [this._eventService.avvEinwilligungImAntrag$,
          this._eventService.vermittlerdatenFuerAvvGesetzt$, this._eventService.uiControls$]).pipe(
        filter(([_, __, ___]) => !!this._keycloak.isAuthenticated()),
        filter(([avvEinwilligung, vermittlerdatenFuerAvvGesetzt, _]) => !!avvEinwilligung?.WertEingabe && vermittlerdatenFuerAvvGesetzt),
        take(1),
        switchMap(([_, __, uiControl]) => {
          const vmr = flatMap(uiControl.uiVorgang.UIArtefakt).filter(
              artefakt => artefakt.FeldnameExtern === '/abschlussvermittlerId')[0];
          const avvData = {} as VermittlerAvvData;
          avvData.vermittlernummer = vmr.WertEingabe;
          return this._http.post<VermittlerAvvData>(this._buildAvvUrlSave(), avvData);
        })
    ).subscribe();
  }

  public handleVermittlerData(uiControl: UiControl): Observable<any> {
    return this._eventService.vermittlerdaten$.pipe(
        take(1),
        map(vermittlerdaten => {
          let hasVermittlerNummerKeyOrEmptyObject = false;
          if (!!vermittlerdaten) {
            // vermittlerdaten != null kommen übers gp-portal zu uns. die sind dann entweder mit {} ein leeres objekt
            // oder wenn der modaldialog im gp-portal mit den vermittlerdaten ausgefüllt wird ein gefülltes objekt.
            // Fall 1: vermittlerdaten={}
            //    a) im gp-portal wird entweder per direkt link über z.b.
            //    https://gp-portal-ui-kons.dmz-cloud-apps.muc.lv1871.de/antrag?produktId=SBUV ein neues produkt geladen: In diesem Fall
            //    gibt es keine vermittlernummer mit der geprüft werden kann ob das avv abgehakt ist oder nicht. Deswegen müssen wir
            //    im avv artefakt die WertEingabe = false setzen. Damit werden die avv artefakte in der ui eingeblendet
            //    b) im gp-portal wird ein gespeicherter antrag geladen:
            //    Dann ist im avv artefakt die WertEingabe gesetzt und wir wissen ob es abgehakt (WertEingabe = true) wurde oder
            //    nicht (WertEingabe = false). Wir setzen dann im avv artefakt die WertEingabe NICHT, im ggs. zu a)
            //
            // Fall2:  vermittlerdaten objekt ist gefüllt, je nachdem wie es im modaldialog im gp-portal ausgefüllt wurde.
            //    Hier führen wir eine Prüfung in der (Mongo-)db durch, wir prüfen mit der vermittlernummer ob das avv abgehakt ist oder
            //    nicht (in dem http.get weiter unten). je nachdem was an response kommt setzen wir dann im avv artefakt die WertEingabe.
            const keys = Object.keys(vermittlerdaten);
            hasVermittlerNummerKeyOrEmptyObject = !keys.length || !!keys.filter(key => key === 'vermittlerNummer').length;
          }
          if (!!vermittlerdaten && hasVermittlerNummerKeyOrEmptyObject && !!uiControl?.uiVorgang?.UIArtefakt) {
            flatMap(uiControl?.uiVorgang?.UIArtefakt)
            .filter(artefakt => !!artefakt.FeldnameExtern)
            .forEach(artefakt => this.mapVermittlerdaten(vermittlerdaten, artefakt));
            this._eventService.vermittlerdaten$.next(null);
            const avv = flatMap(uiControl.uiVorgang.UIArtefakt).filter(
                artefakt => artefakt.FeldnameExtern === '/vermittlerAvv')[0];
            if (!!avv && !StringUtils.isNotNull(avv.WertEingabe)) {
              // nur setzen wenn avv.WertEingabe=null oder gar nicht vorhanden, denn wenns gesetzt ist hat man Fall 1/b, siehe oben
              avv.WertEingabe = false;
            }
            return vermittlerdaten;
          } else {
            // in diesem fall ist das vermittlerdaten Objekt null, ist lokal der Fall. Wir setzen das avv auf
            // abgehakt (d.h. beim avv artefakt die WertEingabe = true) weil es da nicht interessiert
            const avv = flatMap(uiControl.uiVorgang.UIArtefakt).filter(
                artefakt => artefakt.FeldnameExtern === '/vermittlerAvv')[0];
            if (!!avv && !StringUtils.isNotNull(avv.WertEingabe)) {
              avv.WertEingabe = true; // wird das auf false gesetzt, kommt in der ui der block zur AVV
            }
            return null;
          }
        }),
        switchMap(vermittlerdaten =>
            !!vermittlerdaten?.vermittlerNummer && this._keycloak.isAuthenticated() ? this._http.get<VermittlerAvvData>(
                this._buildAvvUrl(normalizeVermittlernummer(vermittlerdaten.vermittlerNummer))) : of(null)
        ),
        map(vermittlerAvv => {
          if (!!vermittlerAvv) {
            const avv = flatMap(uiControl.uiVorgang.UIArtefakt).filter(
                artefakt => artefakt.FeldnameExtern === '/vermittlerAvv')[0];
            if (!!avv) {
              avv.WertEingabe = !!vermittlerAvv.vermittlernummer;
              return uiControl;
            }
          } else {
            return null;
          }
        }),
        catchError(_ => of(null))
    );
  }

  public checkAvvRelevanteArtefakte(uiControl: UiControl) {
    const uiArtefakteMap = new Map<string, UIArtefakt>();
    flatMap(uiControl.uiVorgang.UIArtefakt).forEach(
        artefakt => {
          switch (artefakt.FeldnameExtern) {
            case '/vermittlerAvv':
              uiArtefakteMap.set(artefakt.FeldnameExtern, artefakt);
              break;
            case '/abschlussvermittlerId':
              uiArtefakteMap.set(artefakt.FeldnameExtern, artefakt);
              break;
            case '/vermittlerdatenVorname':
              uiArtefakteMap.set(artefakt.FeldnameExtern, artefakt);
              break;
            case '/vermittlerdatenName':
              uiArtefakteMap.set(artefakt.FeldnameExtern, artefakt);
              break;
            case '/avvEinwilligungAusAntrag':
              uiArtefakteMap.set(artefakt.FeldnameExtern, artefakt);
              break;
          }
        });

    if (!uiArtefakteMap.get('/vermittlerAvv')) {
      return;
    }
    const vnrArtefakt = uiArtefakteMap.get('/abschlussvermittlerId');
    const vnrVornameArtefakt = uiArtefakteMap.get('/vermittlerdatenVorname');
    const vnrNameArtefakt = uiArtefakteMap.get('/vermittlerdatenName');
    if (this._authService.isAuthenticated() && !!vnrArtefakt?.WertEingabe ||
        !this._authService.isAuthenticated() && !!vnrArtefakt?.WertEingabe &&
        !!vnrVornameArtefakt?.WertEingabe && !!vnrNameArtefakt?.WertEingabe) {
      this._eventService.vermittlerdatenFuerAvvGesetzt$.next(true);
    } else {
      this._eventService.vermittlerdatenFuerAvvGesetzt$.next(false);
    }
    const avvEinwilligungArtefakt = uiArtefakteMap.get('/avvEinwilligungAusAntrag');
    this._eventService.avvEinwilligungImAntrag$.next(avvEinwilligungArtefakt);
  }

  private mapVermittlerdaten(vermittlerdaten: Vermittlerdaten, artefakt: UIArtefakt) {
    switch (artefakt.FeldnameExtern) {
      case '/abschlussvermittlerId':
      case '/vertragsbetreuerId':
      case '/dynamikvermittlerId':
      case '/bestandspflegerId':
      case '/vermittlerdatenAbschlussvermittlerId': // deprecated
      case '/vermittlerdatenBetreuerId':// deprecated
      case '/vermittlerdatenDynamikvermittlerId':// deprecated
      case '/vermittlerdatenBestandspflegerId':// deprecated
        if (!!vermittlerdaten.vermittlerNummer) {
          artefakt.WertEingabe = normalizeVermittlernummer(vermittlerdaten.vermittlerNummer);
        }
        break;
      case '/vermittlerdatenFirma':
        if (!!vermittlerdaten.unternehmen?.unternehmensBezeichnung) {
          artefakt.WertEingabe = vermittlerdaten.unternehmen?.unternehmensBezeichnung;
        }
        break;
      case '/vermittlerdatenVorname':
        if (!!vermittlerdaten.unternehmen?.vorname) {
          artefakt.WertEingabe = vermittlerdaten.unternehmen?.vorname;
        }
        break;
      case '/vermittlerdatenName':
        if (!!vermittlerdaten.unternehmen?.nachname) {
          artefakt.WertEingabe = vermittlerdaten.unternehmen?.nachname;
        }
        break;
      case '/vermittlernummerExtern':
      case '/vermittlerdatenExterneVermittlernummer': //deprecated
        if (!!vermittlerdaten.unternehmen?.externeVermittlerNummer) {
          artefakt.WertEingabe = normalizeVermittlernummer(vermittlerdaten.unternehmen?.externeVermittlerNummer);
        }
        break;
      case '/referenznummerExtern':
      case '/vermittlerdatenExterneReferenznummer': //deprecated
        if (!!vermittlerdaten.unternehmen?.externeReferenz) {
          artefakt.WertEingabe = normalizeVermittlernummer(vermittlerdaten.unternehmen?.externeReferenz);
        }
        break;
    }
  }

  private _buildAvvUrl(vmr: string): string {
    if (!vmr) {
      vmr = '0';
    }
    return this._uiConfigService.getAntragserfassungUrl() + '/api/produkt/vermittleravv/' + vmr;
  }

  private _buildAvvUrlSave(): string {
    return this._uiConfigService.getAntragserfassungUrl() + '/api/produkt/savevermittleravv';
  }
}
